import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/errors'
import { env } from '@/lib/env'

const MAX_RETRIES = 2

/** Retry transient faults only — a 404 or a 422 will not fix itself. */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) return false
  return ApiError.from(error).isRetryable
}

function logInDev(scope: string, error: unknown): void {
  if (env.isDev) {
    console.error(`[${scope}]`, ApiError.from(error))
  }
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
        staleTime: env.sharedShow ? 0 : 30_000,
        refetchInterval: env.sharedShow ? 5000 : false,
        gcTime: 5 * 60_000,
        // On mobile every tab switch is a focus event; refetching on reconnect
        // is the signal that actually matters.
        refetchOnWindowFocus: env.sharedShow,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: false,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => logInDev(`query ${String(query.queryKey)}`, error),
    }),
    mutationCache: new MutationCache({
      onError: (error) => logInDev('mutation', error),
    }),
  })
}

export const queryClient = createQueryClient()
